import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    AccountUserResource,
    AccountUserIdsResource,
    AttachAccountUserRequest,
    CompleteRegistrationRequest,
    UpdateAccountUserRequest,
    AccountUsersListParams,
    AccessibleAccountsParams,
    UserApiKeyResponse,
    OAuthTokenResource
} from './api.d';

const createAccountUserApiCall = <T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.ACCOUNT_USER as ServiceName, method, endpoint);

export const accountUserApi = {
    getRegistration: ({accountId, userId, registrationId, options}: {accountId: string; userId: string; registrationId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('GET', `accounts/${accountId}/users/${userId}/registrations/${registrationId}`)({...options}),

    verifyRegistration: ({accountId, userId, registrationId, data, options}: {accountId: string; userId: string; registrationId: string; data: CompleteRegistrationRequest; options?: ApiCallOptions}) =>
        createAccountUserApiCall<OAuthTokenResource>('POST', `accounts/${accountId}/users/${userId}/registrations/${registrationId}`)({data, ...options}),

    completeRegistration: ({accountId, userId, registrationId, data, options}: {accountId: string; userId: string; registrationId: string; data: CompleteRegistrationRequest; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('POST', `accounts/${accountId}/users/${userId}/registrations/${registrationId}/complete`)({data, ...options}),

    confirmRegistration: ({accountId, userId, registrationId, options}: {accountId: string; userId: string; registrationId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<void>('PUT', `accounts/${accountId}/users/${userId}/registrations/${registrationId}/confirm`)({...options}),

    resendRegistration: ({accountId, userId, registrationId, options}: {accountId: string; userId: string; registrationId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<void>('PUT', `accounts/${accountId}/users/${userId}/registrations/${registrationId}/resend`)({...options}),

    getAccountOwner: ({accountId, options}: {accountId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('GET', `accounts/${accountId}/owner`)({...options}),

    getActiveUserIds: ({accountId, options}: {accountId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserIdsResource>('GET', `accounts/${accountId}/users/ids`)({...options}),

    getAccountUser: ({accountUserId, options}: {accountUserId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('GET', `account-users/${accountUserId}`)({...options}),

    getAccountUsers: ({accountId, params, options}: {accountId: string; params?: AccountUsersListParams; options?: ApiCallOptions}) =>
        createAccountUserApiCall<PaginatedResponse<AccountUserResource>>('GET', `accounts/${accountId}/users`)({...options, params}),

    getAccountUserByAccountAndUser: ({accountId, userId, options}: {accountId: string; userId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('GET', `accounts/${accountId}/users/${userId}`)({...options}),

    attachUserToAccount: ({accountId, userId, data, options}: {accountId: string; userId: string; data?: AttachAccountUserRequest; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('POST', `accounts/${accountId}/users/${userId}`)({...options, data}),

    detachUserFromAccount: ({accountId, userId, options}: {accountId: string; userId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<void>('DELETE', `accounts/${accountId}/users/${userId}`)({...options}),

    updateAccountUserStatus: ({accountId, userId, data, options}: {accountId: string; userId: string; data: UpdateAccountUserRequest; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('PATCH', `accounts/${accountId}/users/${userId}`)({...options, data}),

    transferAccountOwnership: ({accountId, userId, options}: {accountId: string; userId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource>('PUT', `accounts/${accountId}/users/${userId}/set-owner`)({...options}),

    getUserAccessibleAccounts: ({userId, params, options}: {userId: string; params?: AccessibleAccountsParams; options?: ApiCallOptions}) =>
        createAccountUserApiCall<AccountUserResource[]>('GET', `users/${userId}/accessible-accounts`)({...options, params}),

    getUserApiKey: ({userId, options}: {userId: string; options?: ApiCallOptions}) =>
        createAccountUserApiCall<UserApiKeyResponse>('GET', `users/${userId}/api-key`)({...options})
};
